<script setup lang="ts">
import { socketEmitAsync } from '@/utils/web-socket'
import { onBeforeMount } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { useCommonStore } from '@/stores/modules/common'
import { useWildStore } from '@/stores/modules/wild'
import MonsterDetail from '@/components/monster-detail/MonsterDetail.vue'

const envMode = import.meta.env.VITE_ENV_MODE

const commonStore = useCommonStore()
const userStore = useUserStore()
const wildStore = useWildStore()
const emits = defineEmits(['start', 'back'])

function getMapItemMonster(mapIndex: number) {
  return wildStore.monsterList.find((item) => item.mapIndex === mapIndex)
}

function buyWildIncense() {
  ElMessageBox.confirm(
    `<p class="affix">购买寻宝熏香后，<span class="info">本月</span>将必定获得<span class="grow">最大数量</span>的<span class="wild">锻造石</span>及<span class="wild">潜能石</span>，请确认是否购买？（仅限本月使用，花费 <span class="diamond">5000钻石</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      if (userStore.diamond < 5000) return ElMessage.warning('钻石不足')
      await socketEmitAsync<NestMonsterType>({ type: 'buyWildIncense' })
      userStore.diamond -= 5000
      if (!wildStore.info) return
      wildStore.info.buyIncense = true
    })
    .catch(() => {})
}
function useWildSupply() {
  if (userStore.consumables.wildSupply <= 0) {
    ElMessageBox.confirm(`<p class="affix"><span class="diamond">荒野补给包</span>不足，是否前往购买？</p>`, '提示', {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    })
      .then(() => {
        commonStore.path = 'Mall'
      })
      .catch(() => {})
    return
  }
  ElMessageBox.confirm(
    `<p class="affix">是否使用<span class="info">荒野补给包</span>，使用后<span class="grow">体力 +5</span>${
      (wildStore.info?.times || 0) > 20 ? '<span class="shink">体力溢出</span>，本次使用将无法获得全额的体力回复，请确认' : ''
    }</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<NestMonsterType>({ type: 'useWildSupply' })
      userStore.consumables.wildSupply--
      if (!wildStore.info) return
      wildStore.info.times += 5
      if (wildStore.info.times >= 30) wildStore.info.times = 30
    })
    .catch(() => {})
}

function wildAction(mapIndex: number) {
  if (!wildStore.canGoMapItemList.includes(mapIndex)) return ElMessage.warning('无法抵达该区域')
  if (wildStore.info?.map.includes(mapIndex)) return ElMessage.warning('已探索该区域')
  if (wildStore.info.times <= 0) return ElMessage.warning('体力不足')
  const localKey = `war-soul-${envMode === 'test' ? 'test-' : ''}wild-tip`
  const isNoTip = localStorage.getItem(localKey)
  if (!wildStore.info.buyIncense && !isNoTip) {
    return ElMessageBox.alert('您暂未购买【寻宝熏香】，请留意，当月只有在购买后再进行挑战时，熏香才会生效', '提示', {
      confirmButtonText: '不再提示',
      callback: () => {
        localStorage.setItem(localKey, '1')
      },
    })
  }

  ElMessageBox.confirm(
    `<p class="affix">确定开始挑战【${getMapItemMonster(mapIndex)?.name}】吗？（不论成败都将<span class="shrink">消耗</span>体力）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'wildAction', data: { index: mapIndex } })
      emits('start', res)
    })
    .catch(() => {})
}

function handleAutoWildAction() {
  if (wildStore.info.times <= 0) return ElMessage.warning('体力不足')
  ElMessageBox.confirm(`<p class="affix">确定【自动】进行大荒野行动吗？（挑战失败或无次数时停止）</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'wildAutoAction' })
      emits('start', res)
    })
    .catch(() => {})
}
</script>

<template>
  <div class="wild-map page">
    <h2>大荒野</h2>
    <p class="base-info">
      <span>
        🔮寻宝熏香：
        <span v-if="!wildStore.info?.buyIncense"
          ><span class="shrink">未生效</span
          ><el-button type="primary" size="small" plain style="margin-left: 5px" @click="buyWildIncense">购买</el-button></span
        >
        <span class="grow" v-else>已生效</span>
      </span>
      <span
        >🧡体力：{{ wildStore.info?.times }} / 50
        <el-button type="primary" size="small" plain style="margin-left: 5px" @click="useWildSupply">补给</el-button></span
      >
    </p>
    <div class="map-wrap">
      <div class="map">
        <table>
          <tr v-for="x in 7">
            <td
              v-for="y in 7"
              :class="{
                'can-go': wildStore.canGoMapItemList.includes((x - 1) * 7 + y - 1),
                already: wildStore.info?.map.includes((x - 1) * 7 + y - 1),
                treasure: getMapItemMonster((x - 1) * 7 + y - 1)?.rank === 4,
              }"
              @click="wildAction((x - 1) * 7 + y - 1)"
            >
              <div class="monster-detail-wrap" @click.stop>
                <MonsterDetail v-if="getMapItemMonster((x - 1) * 7 + y - 1)" :monster="getMapItemMonster((x - 1) * 7 + y - 1)" icon="" :isBig="false">
                  <template #suf>
                    <el-button type="warning" size="small" plain @click="wildAction((x - 1) * 7 + y - 1)" style="width: 100%; margin-top: 10px"
                      >进攻</el-button
                    >
                  </template>
                </MonsterDetail>
              </div>
            </td>
          </tr>
        </table>
      </div>
    </div>
    <CommonBtn color="#ffb3a7" :dark="commonStore.isDarkMode" style="width: 100%; margin-bottom: 5px" @done="handleAutoWildAction">自动</CommonBtn>
    <CommonBtn type="success" style="width: 100%" @done="emits('back')">返回</CommonBtn>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.wild-map {
  padding-top: 0;
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background-color: var(--backColor);
  z-index: 200;
  display: flex;
  flex-flow: column nowrap;
  .base-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    margin-bottom: 8px;
    span {
      display: flex;
      align-items: center;
    }
  }
  .map-wrap {
    max-width: 100%;
    overflow: auto;
    margin-bottom: 10px;
    .map {
      display: flex;
      justify-content: center;
      width: 700px;
      table {
        border-collapse: collapse;
        border: 1px solid #ddd;
        td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: center;
        }
        td {
          width: 100px;
          height: 100px;
          &.can-go {
            background-color: rgba($color: #67c23a, $alpha: 0.5);
          }
          &.already {
            background-color: rgba($color: #e6a23c, $alpha: 0.5);
          }
          &.treasure {
            box-shadow: 0 0 10px #e6d422;
          }
        }
      }
    }
  }
}
</style>
